// Giveaway (raffle) API service — kept separate from the main api.jsx.
//
// The raffle page is part of the admin area (ADMIN only). Participants are
// every customer with a completed order inside a chosen date range — no more
// promo codes, no more provider/customer split (only customers ever qualify).
// Falls back to a realistic mock dataset so the page always renders (e.g.
// during local development without a backend).

import { searchOrders } from '../api';

const MOCK_PARTICIPANTS = [
  { userId: 'm-01', fullName: 'Oleg Cusursuz' },
  { userId: 'm-02', fullName: 'Ana Munteanu' },
  { userId: 'm-03', fullName: 'Дмитрий Соколов' },
  { userId: 'm-04', fullName: 'Mihai Ciobanu' },
  { userId: 'm-05', fullName: 'Елена Прокопенко' },
  { userId: 'm-06', fullName: 'Victor Rusu' },
  { userId: 'm-07', fullName: 'Наталья Иванова' },
  { userId: 'm-08', fullName: 'Cristina Lungu' },
  { userId: 'm-09', fullName: 'Андрей Морозов' },
  { userId: 'm-10', fullName: 'Daniela Postică' },
  { userId: 'm-11', fullName: 'Сергей Волков' },
  { userId: 'm-12', fullName: 'Ion Vârlan' },
  { userId: 'm-13', fullName: 'Оксана Левченко' },
  { userId: 'm-14', fullName: 'Radu Ștefănescu' },
  { userId: 'm-15', fullName: 'Марина Тимофеева' },
  { userId: 'm-16', fullName: 'Vlad Țurcanu' },
];

// Accepts either a Spring Page ({ content: [...] }) or a bare array.
export const normalizeParticipants = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : [];
  return list
    .filter((item) => item && (item.fullName || item.name))
    .map((item, index) => ({
      userId: item.userId ?? item.id ?? `participant-${index}`,
      fullName: String(item.fullName || item.name).trim(),
    }));
};

const ORDERS_PAGE_SIZE = 100;
const MAX_ORDERS = 500; // upper bound on orders scanned per range
const MAX_PAGES = 50; // safety net in case the backend caps the page size

// Walk the orders that moved to DONE inside the range: one order = one entry, so
// a customer with several completed orders gets several chances. Only
// { userId, fullName, orderId } is kept — the rest of each (heavy) order DTO is
// dropped page by page. Sorted by the immutable createdAt so rows don't shift
// between pages while we read them.
const collectParticipantsFromOrders = async (doneFrom, doneTo) => {
  const seenOrders = new Set();
  const participants = [];

  for (let page = 0; page < MAX_PAGES && seenOrders.size < MAX_ORDERS; page += 1) {
    const data = await searchOrders({
      doneFrom,
      doneTo,
      page,
      size: ORDERS_PAGE_SIZE,
      sort: 'createdAt',
      direction: 'ASC',
    });
    const content = Array.isArray(data?.content) ? data.content : [];

    for (const order of content) {
      if (seenOrders.size >= MAX_ORDERS) break;
      if (!order?.id || seenOrders.has(order.id)) continue;
      seenOrders.add(order.id);

      const profile = order.userProfileForOrderDto;
      const userId = profile?.userProfileId;
      const fullName = String(profile?.fullName || '').trim();
      if (!userId || !fullName) continue;
      participants.push({ userId, fullName, orderId: order.id });
    }

    const totalPages = data?.totalPages ?? data?.page?.totalPages;
    const total = data?.totalElements ?? data?.page?.totalElements;
    if (seenOrders.size >= MAX_ORDERS && total > MAX_ORDERS) {
      console.warn(`Raffle: ${total} completed orders in range, only the first ${MAX_ORDERS} were counted`);
    }
    if (!content.length || data?.last === true || (totalPages != null && page + 1 >= totalPages)) break;
  }

  return participants;
};

/**
 * Fetch every customer with an order completed (DONE) in [startDate, endDate].
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<{ participants: Array, source: 'api' | 'mock' }>}
 */
export const fetchRaffleParticipants = async (startDate, endDate) => {
  try {
    if (!startDate || !endDate) throw new Error('Missing date range');
    const participants = await collectParticipantsFromOrders(
      startDate.toISOString(),
      endDate.toISOString()
    );
    return { participants, source: 'api' };
  } catch (error) {
    return { participants: normalizeParticipants(MOCK_PARTICIPANTS), source: 'mock' };
  }
};

const raffleApi = {
  fetchRaffleParticipants,
  normalizeParticipants,
};

export default raffleApi;
